import React, { useEffect, useState } from 'react';
import { Alert, Col, Form, Modal, Row } from 'react-bootstrap';
import { FloatingLabel } from 'react-bootstrap';
import LazyLoad from 'react-lazyload';
import { FaFilePdf } from 'react-icons/fa';
import styles from './fileUpload.module.scss';
import { RxCross2 } from 'react-icons/rx';

// Utility function to generate timestamp-based filename
const convertToImageRename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `_${year}${month}${day}_${hours}${minutes}${seconds}`;
};

// Default upload function - can be overridden via props
const defaultUploadFunction = async (fileData) => {
  console.warn(
    'Default upload function called. Please provide uploadFunction prop for actual upload.'
  );
  // Simulate upload delay
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return { url: 'https://via.placeholder.com/150' };
};

function ImageUpload(props) {
  // Destructure props with default values for better reusability
  const {
    // Required props
    id = 'file-upload',
    cid = 'file-input',
    label = 'Choose File',
    fileType = '.png,.jpg,.jpeg,.bmp,.pdf',
    typ = 'image',
    imgName = 'file',

    // Optional props with defaults
    value = '',
    image = null,
    hide = false,
    imgValidation = false,

    // Callback functions
    uploading = () => {},
    responseUrl = () => {},

    // Custom upload function
    uploadFunction = defaultUploadFunction,

    // Customization options
    maxFileSize = 204800, // 200KB default
    maxWidth = { png: 500, other: 1000 },
    maxHeight = { png: 500, other: 1000 },
    quality = 0.8,
    showPreview = true,
    previewSize = 80,
    alertTimeout = 5000,

    // Color customization props
    successColor = '#28a745', // Default Bootstrap success color
    dangerColor = '#dc3545', // Default Bootstrap danger color

    // Custom messages
    messages = {
      selectFile: 'Please Select a File',
      uploading: 'Uploading The Image...',
      uploaded: 'Successfully Uploaded',
      failed: 'Failed to Upload the Image',
      sizeError: 'Image should not exceed more than 200 kb',
      fileTypeError:
        'Please upload files having extensions .PNG, .JPG, .JPEG, .BMP or .PDF only.',
    },
  } = props;

  const [imageMessage, setImageMessage] = useState(messages.selectFile);
  const [logoUploadStatus, setLogoUploadStatus] = useState('');
  const [popupImgUrl, setPopupImgUrl] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [alertShow, setAlertShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVarient, setAlertVarient] = useState('');
  const handleClose = () => {
    setShowPopup(false);
  };

  /* Hide Notification */
  function hidemessage() {
    setTimeout(function () {
      setImageMessage('');
      if (document.querySelector(`#${id}`)) {
        document.querySelector(`#${id}`).classList.remove('errorBrd');
      }
      setAlertShow(false);
      setAlertMessage('');
      setAlertVarient('');
    }, alertTimeout);
  }

  /* This hook is used to whenever the condition in the brackets is true. */
  useEffect(() => {
    if (document.getElementById(cid)) {
      document.getElementById(cid).value = '';
    }
    setLogoUploadStatus('');
    uploading(false);
    setImageMessage(messages.selectFile);
  }, [hide === true || imgValidation === true]);

  /* OnChange event we will call this and update the Image */
  const handleChangeImgUpload = async (e, typ) => {
    if (document.querySelector(`#${id}`)) {
      document.querySelector(`#${id}`).classList.remove('errorBrd');
    }

    const { files } = e.target;
    if (!files || files.length === 0) return;

    const newName =
      imgName +
      convertToImageRename(new Date()) +
      (files[0].type === 'image/jpeg'
        ? files[0].name.slice(-5)
        : files[0].name.slice(-4));

    const newFile = new File([files[0]], newName, {
      type: files[0].type,
      lastModified: files[0].lastModified,
    });

    const compressedImage = async (data) => {
      if (data.size < maxFileSize) {
        setLogoUploadStatus('image-uploading');
        uploading(true);

        let obj = {
          image: data,
          type: typ,
        };

        try {
          const response = await uploadFunction(obj);
          if (response && response.url) {
            responseUrl(response.url);
            setLogoUploadStatus('image-uploaded');
            uploading(false);
          } else {
            setAlertMessage(messages.failed);
            setAlertShow(true);
            setAlertVarient('danger');
            hidemessage();
            setLogoUploadStatus('');
            uploading(false);
            if (e.target) e.target.value = '';
          }
        } catch (error) {
          console.error('Upload error:', error);
          setAlertMessage(messages.failed);
          setAlertShow(true);
          setAlertVarient('danger');
          hidemessage();
          setLogoUploadStatus('');
          uploading(false);
          if (e.target) e.target.value = '';
        }
      } else {
        if (document.querySelector(`#${id}`)) {
          document.querySelector(`#${id}`).classList.add('errorBrd');
        }
        if (e.target) e.target.value = '';
        setImageMessage(messages.sizeError);
        setLogoUploadStatus('');
        hidemessage();
        uploading(false);
        responseUrl();
      }
    };

    if (validateFileType(files[0])) {
      if (files[0].type === 'application/pdf') {
        compressedImage(newFile);
      } else {
        const MAX_WIDTH =
          files[0].type === 'image/png' ? maxWidth.png : maxWidth.other;
        const MAX_HEIGHT =
          files[0].type === 'image/png' ? maxHeight.png : maxHeight.other;
        const MIME_TYPE = files[0].type;

        const file = files[0];
        const blobURL = URL.createObjectURL(file);
        const img = new Image();
        img.src = blobURL;

        img.onerror = function () {
          URL.revokeObjectURL(this.src);
          console.log('Cannot load image');
        };

        img.onload = function () {
          URL.revokeObjectURL(this.src);
          const [newWidth, newHeight] = calculateSize(
            img,
            MAX_WIDTH,
            MAX_HEIGHT
          );
          const canvas = document.createElement('canvas');
          canvas.width = newWidth;
          canvas.height = newHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], newName, {
                lastModified: file.lastModified,
                lastModifiedDate: file.lastModifiedDate,
                type: file.type,
              });
              compressedImage(compressedFile);
            },
            MIME_TYPE,
            quality
          );
        };

        // This is used to change the size of the compressed image.
        const calculateSize = (img, maxWidth, maxHeight) => {
          let width = img.width;
          let height = img.height;

          // calculate the width and height, constraining the proportions
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          return [width, height];
        };
      }
    }
  };

  const validateFileType = (file) => {
    const allowedExtensions =
      fileType === '.png,.jpg,.jpeg,.bmp'
        ? /(\.jpg|\.png|\.jpeg|\.bmp)$/i
        : /(\.jpg|\.png|\.jpeg|\.bmp|\.pdf)$/i;

    if (!allowedExtensions.exec(file.name)) {
      setAlertMessage(messages.fileTypeError);
      setAlertShow(true);
      setAlertVarient('danger');
      hidemessage();
      return false;
    }
    return true;
  };

  const handleConfirmPopup = (data) => {
    setShowPopup(true);
    setPopupImgUrl(data);
  };

  return (
    <>
      <Alert
        className={styles.notification}
        variant={alertVarient}
        show={alertShow}
        onClose={() => setAlertShow(false)}
        dismissible
        style={{
          backgroundColor: alertVarient === 'danger' ? dangerColor : undefined,
          borderColor: alertVarient === 'danger' ? dangerColor : undefined,
          color: alertVarient === 'danger' ? 'white' : undefined,
        }}
      >
        <span className={styles.alertContent}>{alertMessage}</span>
      </Alert>
      <Row className={styles.fileUploadContainer}>
        <Col>
          <FloatingLabel
            label={label}
            className={styles.floatingLabel}
            value={value}
          >
            <Form.Control
              type="file"
              accept={fileType}
              id={cid}
              className={styles.formControl}
              onChange={(e) => handleChangeImgUpload(e, typ)}
            />
            <Form.Control.Feedback className={styles.feedback}>
              {imageMessage}
            </Form.Control.Feedback>
            {logoUploadStatus === 'image-uploading' && (
              <div
                className={`${styles.statusMessage} ${styles.uploadingStatus}`}
                style={{ color: '#198dc8' }}
              >
                {messages.uploading}
              </div>
            )}
            {logoUploadStatus === 'image-uploaded' && (
              <div
                className={`${styles.statusMessage} ${styles.uploadedStatus}`}
                style={{ color: successColor }}
              >
                {messages.uploaded}
              </div>
            )}
          </FloatingLabel>
        </Col>
        {showPreview && image && (
          <Col md="3">
            <div className={styles.uploadedImage}>
              <LazyLoad>
                {image.slice(-3) !== 'pdf' ? (
                  <img
                    onClick={() => handleConfirmPopup(image)}
                    alt="Uploaded file"
                    target="_blank"
                    rel="noopener noreferrer"
                    src={image}
                  />
                ) : (
                  <a
                    href={image}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.pdfLink}
                  >
                    <FaFilePdf size={40} />
                  </a>
                )}
              </LazyLoad>
            </div>
          </Col>
        )}
      </Row>
      <Modal size="lg" show={showPopup} onHide={handleClose}>
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body className="popupImg">
          <img src={popupImgUrl} alt={'popupImgUrl'} className="w-100" />
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ImageUpload;
